import React, { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";
import { NAV } from "@/constants/testIds";
import { ASSETS } from "@/constants/site";
import { Menu, X } from "lucide-react";

const NavLink = ({ href, children, testid, onClick }) => (
  <a
    data-testid={testid}
    href={href}
    onClick={onClick}
    className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-300 hover:text-[#F5B800] transition-colors duration-200"
  >
    {children}
  </a>
);

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = "block md:inline";

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav
      data-testid={NAV.root}
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? "bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-[140px] flex items-center justify-between">
        <a data-testid={NAV.logo} href="#top" className="flex items-center gap-3">
          <img
            src={ASSETS.logo}
            alt="Trom Motors"
            className="trom-logo h-28 md:h-36 w-auto object-contain"
          />
        </a>

        <div className="hidden md:flex items-center gap-10">
          <NavLink testid={NAV.linkAbout} href="#about">{t.nav.about}</NavLink>
          <NavLink testid={NAV.linkCapabilities} href="#capabilities">{t.nav.capabilities}</NavLink>
          <NavLink testid={NAV.linkFleet} href="#fleet">{t.nav.fleet}</NavLink>
          <NavLink testid={NAV.linkContact} href="#contact">{t.nav.contact}</NavLink>
        </div>

        <div className="flex items-center gap-6">
          <div className="hidden sm:flex items-center gap-1 text-xs font-bold tracking-widest">
            <button
              data-testid={NAV.langEn}
              onClick={() => setLang("en")}
              className={`px-2 py-1 uppercase transition-colors ${lang === "en" ? "text-[#F5B800]" : "text-neutral-500 hover:text-white"}`}
            >
              EN
            </button>
            <span className="text-neutral-700">/</span>
            <button
              data-testid={NAV.langEs}
              onClick={() => setLang("es")}
              className={`px-2 py-1 uppercase transition-colors ${lang === "es" ? "text-[#F5B800]" : "text-neutral-500 hover:text-white"}`}
            >
              ES
            </button>
          </div>

          <a
            data-testid={NAV.quoteBtn}
            href="#contact"
            className="hidden md:inline-flex btn-primary sharp"
          >
            {t.nav.quote}
          </a>

          <button
            data-testid={NAV.mobileMenuBtn}
            className="md:hidden text-white p-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-[#0A0A0A] border-t border-white/5 px-6 py-6 flex flex-col gap-5">
          <NavLink testid={`${NAV.linkAbout}-m`} href="#about" onClick={closeMobile}>{t.nav.about}</NavLink>
          <NavLink testid={`${NAV.linkCapabilities}-m`} href="#capabilities" onClick={closeMobile}>{t.nav.capabilities}</NavLink>
          <NavLink testid={`${NAV.linkFleet}-m`} href="#fleet" onClick={closeMobile}>{t.nav.fleet}</NavLink>
          <NavLink testid={`${NAV.linkContact}-m`} href="#contact" onClick={closeMobile}>{t.nav.contact}</NavLink>
          <div className="flex items-center gap-2 pt-3 border-t border-white/5">
            <button
              onClick={() => setLang("en")}
              className={`px-3 py-1 text-xs font-bold uppercase ${lang === "en" ? "text-[#F5B800]" : "text-neutral-500"}`}
            >EN</button>
            <span className="text-neutral-700">/</span>
            <button
              onClick={() => setLang("es")}
              className={`px-3 py-1 text-xs font-bold uppercase ${lang === "es" ? "text-[#F5B800]" : "text-neutral-500"}`}
            >ES</button>
          </div>
        </div>
      )}
    </nav>
  );
}
